/**
 * WelcomeDialog Component
 *
 * One-time welcome dialog shown to new users on their first authenticated page visit.
 * Congratulates them on signing up and informs them about their free credits.
 * Features confetti, bounce animation, and sparkle effects matching the
 * purchase success dialog style.
 *
 * Dismissal is persisted to the user profile in DynamoDB via PATCH /profile.
 *
 * Requirements: 1.1, 3.1
 */

import { useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
} from '@mui/material';
import { Celebration, AutoAwesome } from '@mui/icons-material';
import confetti from 'canvas-confetti';

export interface WelcomeDialogProps {
    open: boolean;
    onClose: () => void;
    username?: string;
}

export function WelcomeDialog({ open, onClose, username }: WelcomeDialogProps) {
    const displayName = username || 'there';

    // Fire confetti when dialog opens
    useEffect(() => {
        if (!open) return;
        const colors = [
            '#ff0000', '#ff6600', '#ffdd00', '#00ff00',
            '#00ccff', '#6633ff', '#ff33cc', '#ffffff',
            '#4caf50', '#2196f3',
        ];

        // Bring confetti canvas above the MUI Dialog backdrop (z-index 1300)
        const canvas = document.querySelector('canvas.confetti-canvas') as HTMLCanvasElement | null;
        if (canvas) {
            canvas.style.zIndex = '1400';
        }

        const opts = { colors, scalar: 1.4, ticks: 200, zIndex: 1400 };

        confetti({
            ...opts,
            particleCount: 200,
            spread: 100,
            origin: { x: 0.5, y: 0.5 },
            startVelocity: 45,
        });

        // Side cannons — single burst
        confetti({ ...opts, particleCount: 80, angle: 60, spread: 70, origin: { x: 0, y: 0.65 }, startVelocity: 35 });
        confetti({ ...opts, particleCount: 80, angle: 120, spread: 70, origin: { x: 1, y: 0.65 }, startVelocity: 35 });
    }, [open]);

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="xs"
                fullWidth
                disableEnforceFocus
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        textAlign: 'center',
                        py: 1,
                        overflow: 'visible',
                        boxShadow: '0 0 40px rgba(76,175,80,0.2), 0 0 80px rgba(33,150,243,0.1)',
                        animation: 'welcomeBounceIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    },
                }}
            >
                <DialogContent
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                        pt: 4,
                    }}
                >
                    {/* Decorative icons */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 1 }}>
                        <AutoAwesome sx={{ fontSize: 36, color: '#ffd54f', animation: 'welcomePulse 1.5s infinite' }} />
                        <Celebration sx={{ fontSize: 52, color: '#4caf50' }} />
                        <AutoAwesome sx={{ fontSize: 36, color: '#ce93d8', animation: 'welcomePulse 1.5s infinite 0.5s' }} />
                    </Box>

                    <Typography variant="h5" fontWeight="bold" sx={{ color: '#a78bfa' }}>
                        Welcome, {displayName}!
                    </Typography>

                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                        We&apos;re glad you&apos;re here. To get you started,
                        we&apos;ve added{' '}
                        <Box component="span" sx={{ color: '#4caf50', fontWeight: 700 }}>
                            100 credits
                        </Box>{' '}
                        to your account. Start creating today!
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                    <Button
                        variant="contained"
                        onClick={onClose}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 4,
                            background: 'linear-gradient(135deg, #4caf50 0%, #2196f3 100%)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #43a047 0%, #1e88e5 100%)',
                            },
                        }}
                    >
                        Let&apos;s Go!
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Animations */}
            <style>{`
                @keyframes welcomePulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.5; transform: scale(1.2); }
                }
                @keyframes welcomeBounceIn {
                    0% { opacity: 0; transform: scale(0.3); }
                    50% { opacity: 1; transform: scale(1.05); }
                    100% { transform: scale(1); }
                }
            `}</style>
        </>
    );
}
